"use client";

import clsx from "clsx";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";
import { UpdateOrg } from "@/app/api/org/updateOrg";
import SubscriptionModal from "@/components/common/SubscriptionModal";
import FancyLoader from "@/components/common/fancyLoader";
import Button from "@/components/library/Button";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import Modal from "@/components/library/Modal";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { MainAlertProps } from "@/types/common/alerts";
import { HandleOpenBillingSession } from "@/utils/agencies/settings";
import { apiFetch } from "@/lib/api";
import { shouldShowTermsAcceptanceModal } from "@/utils/common/admin";
import { BASE_MARKETING_URL } from "@/utils/common/baseUrls";

type GenericModalProps = {
  open: boolean;
  message: string;
  description?: string;
};

type AlertProps = {
  setAlert: (settings: MainAlertProps) => void;
  dismissAlert: () => void;
  showToast: boolean;
  toastSettings: MainAlertProps;
  isLoading: GenericModalProps;
  isSaving: GenericModalProps;
  isConfirming: GenericModalProps & {
    mode: "delete" | "confirm" | "choice" | "unsaved";
    choices?: string[];
    saveCallback: () => void;
    proceedCallback: () => void;
    customBody?: React.ReactNode;
  };
  setIsLoading: (
    isSaving: boolean,
    message: string,
    description?: string
  ) => void;
  setIsSaving: (
    isSaving: boolean,
    message: string,
    description?: string
  ) => void;
  setIsConfirming: (
    isConfirming: boolean,
    mode: "delete" | "confirm" | "choice" | "unsaved",
    message: string,
    saveCallback: () => Promise<void>,
    proceedCallback: () => Promise<void>,
    choices?: string[],
    customBody?: React.ReactNode
  ) => void;
  handleApi: <T1, T2 = T1>(
    url: string,
    method: "POST" | "PUT" | "DELETE" | "PATCH",
    data: T1,
    isSaving: boolean,
    text:
      | {
        saving: string;
        error: string;
        success: string;
      }
      | ((val: string) => string),
    isSilent?: boolean
  ) => Promise<T2 | null>;
};

const getIcon = (
  status: "success" | "error" | "warning" | "info"
):
  | "circle-check"
  | "circle-exclamation"
  | "triangle-exclamation"
  | "circle-info" => {
  switch (status) {
    case "success":
      return "circle-check";
    case "error":
      return "circle-exclamation";
    case "warning":
      return "triangle-exclamation";
    case "info":
      return "circle-info";
  }
};

type AlertToastRendererProps = {
  showToast: boolean;
  toastSettings: MainAlertProps;
  dismissAlert: () => void;
};

function AlertToastRenderer({
  showToast,
  toastSettings,
  dismissAlert,
}: Readonly<AlertToastRendererProps>) {
  useEffect(() => {
    if (!showToast) {
      toast.dismiss("gail-alert");
      return;
    }

    const icon = getIcon(toastSettings.status);
    const colorClass =
      toastSettings.status === "success"
        ? "text-green-500"
        : toastSettings.status === "error"
          ? "text-red-500"
          : toastSettings.status === "warning"
            ? "text-yellow-500"
            : "text-blue-500";

    toast.custom(
      () => (
        <div
          className={clsx(
            "flex items-start gap-3 max-w-sm rounded-lg border p-4 shadow-lg",
            "bg-panel !text-gray-900 border-300",
            "dark:bg-panel_secondary dark:!text-gray-100 dark:border-600"
          )}
        >
          <Icon icon={icon} className={colorClass} size={ICON_SIZES.XL} />
          <div className="flex-1 min-w-0 pr-2 pl-1 overflow-hidden">
            <div
              className={clsx(
                "gap-1 grid w-full font-normal text-sm break-words",
                "!text-gray-900 dark:!text-gray-100"
              )}
            >
              {toastSettings.message &&
                toastSettings.message
                  .split("\n")
                  .map((line, i) => <div key={i}>{line}</div>)}
            </div>
            {toastSettings.btnLabel && toastSettings.btnAction && (
              <Button
                compact
                className={clsx("mt-1", colorClass)}
                type="tertiary"
                onClick={() => {
                  toastSettings.btnAction?.();
                  dismissAlert();
                }}
              >
                {toastSettings.btnLabel}
              </Button>
            )}
          </div>
          <button
            type="button"
            aria-label="Dismiss notification"
            className={clsx(
              "ml-auto rounded p-1 transition-colors",
              "text-600 hover:text-900 hover:bg-200",
              "dark:text-300 dark:hover:text-50 dark:hover:bg-700"
            )}
            onClick={() => dismissAlert()}
          >
            x
          </button>
        </div>
      ),
      {
        id: "gail-alert",
        duration: toastSettings.secondsToClose
          ? toastSettings.secondsToClose * 1000
          : Infinity,
      }
    );
  }, [showToast, toastSettings, dismissAlert]);

  return null;
}

export const AlertContext = createContext<AlertProps>({
  setAlert: () => {},
  dismissAlert: () => {},
  showToast: false,
  toastSettings: {
    status: "success",
    message: "",
  },
  isLoading: {
    open: false,
    message: "",
  },
  isSaving: {
    open: false,
    message: "",
    description: undefined,
  },
  isConfirming: {
    open: false,
    message: "",
    mode: "confirm",
    choices: undefined,
    saveCallback: () => {},
    proceedCallback: () => {},
  },
  setIsLoading: () => {},
  setIsSaving: () => {},
  setIsConfirming: () => {},
  handleApi: () => Promise.resolve(null),
});

export const useAlert = () => useContext(AlertContext)!;

const useAlertFunction = (): AlertProps => {
  //Private State
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  //Public State
  const [showToast, setShowToast] = useState(false);
  const [toastSettings, setToastSettings] = useState<MainAlertProps>({
    status: "success",
    message: "",
  });
  const [isSaving, setIsSaving] = useState<GenericModalProps>({
    open: false,
    message: "",
  });
  const [isLoading, setIsLoading] = useState<GenericModalProps>({
    open: false,
    message: "",
    description: "",
  });
  const [isConfirming, setIsConfirming] = useState<
    GenericModalProps & {
      mode: "delete" | "confirm" | "choice" | "unsaved";
      choices?: string[];
      customBody?: React.ReactNode;
      saveCallback: () => void;
      proceedCallback: () => void;
    }
  >({
    open: false,
    message: "",
    mode: "confirm",
    choices: undefined,
    customBody: undefined,
    saveCallback: () => {},
    proceedCallback: () => {},
  });

  //Public Methods
  const setAlert = (settings: MainAlertProps) => {
    setToastSettings(settings);
    setShowToast(true);

    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    const secondsToClose = settings.secondsToClose || 0;

    if (secondsToClose > 0) {
      const timeout = setTimeout(() => {
        setShowToast(false);
      }, secondsToClose * 1000);

      setTimeoutId(timeout);
    }
  };

  const dismissAlert = () => {
    setShowToast(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  async function handleApi<T1, T2 = T1>(
    url: string,
    method: "POST" | "PUT" | "DELETE" | "PATCH",
    data: T1,
    isSaving: boolean,
    text:
      | {
        saving: string;
        error: string;
        success: string;
      }
      | ((val: string) => string),
    isSilent: boolean = false
  ): Promise<T2 | null> {
    const textIsFunction = typeof text === "function";
    const textStrings = {
      saving: textIsFunction ? text("saving") : text.saving,
      error: textIsFunction ? text("error") : text.error,
      success: textIsFunction ? text("success") : text.success,
    };

    const statusFunc = isSaving ? setIsSaving : setIsLoading;

    if (!isSilent) {
      statusFunc({ open: true, message: textStrings.saving });
    }

    const response = await apiFetch<T2>(url, method, { body: data });

    if (!isSilent) {
      statusFunc({ open: false, message: "" });
    }

    if (!response) {
      if (!isSilent) {
        setAlert({
          status: "error",
          message: textStrings.error,
          secondsToClose: 10,
        });
      }

      return null;
    }

    if (textStrings.success && !isSilent) {
      setAlert({
        status: "success",
        message: textStrings.success,
        secondsToClose: 4,
      });
    }

    return response;
  }

  return {
    setAlert,
    dismissAlert,
    showToast,
    toastSettings,
    isLoading,
    setIsLoading: (isLoading, message, description) => {
      setIsLoading({
        open: isLoading,
        message: message,
        description: description,
      });
    },
    isSaving,
    setIsSaving: (isSaving, message, description) => {
      setIsSaving({
        open: isSaving,
        message: message,
        description: description,
      });
    },
    isConfirming,
    setIsConfirming: (
      isConfirming,
      mode,
      message,
      saveCallback,
      proceedCallback,
      choices,
      customBody
    ) => {
      setIsConfirming({
        open: isConfirming,
        message: message,
        mode: mode,
        choices: choices,
        customBody: customBody,
        saveCallback: saveCallback,
        proceedCallback: proceedCallback,
      });
    },
    handleApi,
  };
};

export const AlertContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const alert = useAlertFunction();
  const { subscription, user } = useAuthContext();
  const { t: ot } = useTranslation("onboarding");
  const { t: ct } = useTranslation("common");

  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState({
    show: false,
    clientSecret: "",
  });
  const [showLoadingModal, setShowLoadingModal] = useState({
    show: false,
    title: "",
    description: "",
  });

  const {
    dismissAlert,
    showToast,
    toastSettings,
    isLoading,
    isSaving,
    isConfirming,
    setIsConfirming,
  } = alert;

  const shouldShowTermsModal = shouldShowTermsAcceptanceModal(
    subscription.flags.needs_signing,
    user
  );

  useEffect(() => {
    setShowTermsModal(shouldShowTermsModal);
  }, [shouldShowTermsModal]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <AlertContext.Provider value={alert}>
      {children}
      {mounted && (
        <>
          <Toaster position="top-right" />
          <AlertToastRenderer
            showToast={showToast}
            toastSettings={toastSettings}
            dismissAlert={dismissAlert}
          />
        </>
      )}
      <Modal
        open={showTermsModal}
        onOpenChange={() => {}}
        header={{
          title: ot("termsModalTitle"),
          className: "[&_button]:hidden",
        }}
        buttons={[
          {
            type: "primary",
            onClick: async () => {
              setShowTermsModal(false);
              if (!shouldShowTermsModal) {
                return;
              }

              setShowLoadingModal({
                show: true,
                title: "Activating Your Account",
                description: "Activating Your Account",
              });

              await UpdateOrg({
                details: {
                  needs_signing: false,
                },
              });

              setShowLoadingModal({
                show: false,
                title: "",
                description: "",
              });
            },
            children: ct("iAgree"),
          },
        ]}
      >
        <p>{ot("termsModalDescription")}</p>
        <p className={clsx("mt-4")}>{ot("termsModalDetails")}</p>
        <div className={clsx("mt-4", "grid", "grid-cols-2", "gap-4")}>
          <Button
            type="primary"
            href={`${BASE_MARKETING_URL}/terms-conditions`}
            target="_blank"
          >
            {ot("termsAndConditions")}
          </Button>
          <Button
            type="primary"
            href={`${BASE_MARKETING_URL}/privacy-policy`}
            target="_blank"
          >
            {ot("privacyPolicy")}
          </Button>
        </div>
      </Modal>
      <Modal
        open={showBillingModal}
        onOpenChange={(open) => !open && setShowBillingModal(false)}
        header={{
          title: ct("accountNotActiveTitle"),
        }}
        buttons={[
          {
            type: "primary",
            onClick: () => {
              setShowBillingModal(false);
              HandleOpenBillingSession(alert.setAlert);
            },
            iconBefore: "address-card",
            children: ct("updateBilling"),
          },
        ]}
      >
        <p>{ct("accountNotActiveDescription")}</p>
      </Modal>
      <Modal
        open={showLoadingModal.show}
        header={{ title: showLoadingModal.description }}
        onOpenChange={() => {}}
      >
        <FancyLoader />
      </Modal>
      {(() => {
        const messageLines = isConfirming.message.split("\n");
        const title = messageLines[0];
        const bodyLines = messageLines.slice(1);
        return (
      <Modal
        open={isConfirming.open}
        onOpenChange={(open) => {
          if (!open) {
            setIsConfirming(
              false,
              "confirm",
              "",
              async () => {},
              async () => {}
            );
          }
        }}
        header={{
          title,
          description:
            isConfirming.mode === "unsaved" && bodyLines.length > 0
              ? bodyLines[0]
              : undefined,
        }}
        buttons={[
          {
            type: isConfirming.mode === "unsaved" ? "tertiary" : "secondary",
            wide: true,
            onClick: async () => {
              await isConfirming.proceedCallback();
              setIsConfirming(
                false,
                "confirm",
                "",
                async () => {},
                async () => {}
              );
            },
            children:
              isConfirming.mode === "delete"
                ? ct("cancel")
                : isConfirming.mode === "unsaved"
                  ? ct("unsavedData.discard")
                  : isConfirming.mode === "confirm"
                    ? ct("continueAnyways")
                    : isConfirming.choices && isConfirming.choices.length > 1
                      ? isConfirming.choices[1]
                      : ct("noWord"),
          },
          {
            type: "primary",
            wide: true,
            onClick: async () => {
              await isConfirming.saveCallback();
              setIsConfirming(
                false,
                "confirm",
                "",
                async () => {},
                async () => {}
              );
            },
            children:
              isConfirming.mode === "delete"
                ? ct("remove")
                : isConfirming.mode === "unsaved"
                  ? ct("unsavedData.saveChanges")
                  : isConfirming.mode === "confirm"
                    ? ct("saveAndContinue")
                    : isConfirming.choices && isConfirming.choices.length > 0
                      ? isConfirming.choices[0]
                      : ct("yes"),
          },
        ]}
      >
        {isConfirming.mode !== "unsaved" &&
          bodyLines.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        {!!isConfirming.customBody && isConfirming.customBody}
      </Modal>
        );
      })()}
      <SubscriptionModal
        show={showSubscriptionModal.show}
        clientSecret={showSubscriptionModal.clientSecret}
        onClose={() => {
          setShowSubscriptionModal({
            show: false,
            clientSecret: showSubscriptionModal.clientSecret,
          });
        }}
      />
      <Modal
        open={isLoading.open}
        header={{
          title: isLoading.message,
          description: isLoading.description,
        }}
        onOpenChange={() => {}}
      >
        <FancyLoader />
      </Modal>
      <Modal
        open={isSaving.open}
        header={{
          title: isSaving.message,
          description: isSaving.description,
        }}
        onOpenChange={() => {}}
      >
        <FancyLoader />
      </Modal>
    </AlertContext.Provider>
  );
};

export const SimpleAlertContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const alert = useAlertFunction();
  const { t: ct } = useTranslation("common");

  const {
    dismissAlert,
    showToast,
    toastSettings,
    isLoading,
    isSaving,
    isConfirming,
    setIsConfirming,
  } = alert;

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <AlertContext.Provider value={alert}>
      {children}
      {mounted && (
        <>
          <Toaster position="top-right" />
          <AlertToastRenderer
            showToast={showToast}
            toastSettings={toastSettings}
            dismissAlert={dismissAlert}
          />
        </>
      )}
      {(() => {
        const messageLines = isConfirming.message.split("\n");
        const title = messageLines[0];
        const bodyLines = messageLines.slice(1);
        return (
      <Modal
        open={isConfirming.open}
        onOpenChange={(open) => {
          if (!open) {
            setIsConfirming(
              false,
              "confirm",
              "",
              async () => {},
              async () => {}
            );
          }
        }}
        header={{
          title,
          description:
            isConfirming.mode === "unsaved" && bodyLines.length > 0
              ? bodyLines[0]
              : undefined,
        }}
        buttons={[
          {
            type: isConfirming.mode === "unsaved" ? "tertiary" : "secondary",
            wide: true,
            onClick: async () => {
              await isConfirming.proceedCallback();
              setIsConfirming(
                false,
                "confirm",
                "",
                async () => {},
                async () => {}
              );
            },
            children:
              isConfirming.mode === "delete"
                ? ct("cancel")
                : isConfirming.mode === "unsaved"
                  ? ct("unsavedData.discard")
                  : isConfirming.mode === "confirm"
                    ? ct("continueAnyways")
                    : isConfirming.choices && isConfirming.choices.length > 1
                      ? isConfirming.choices[1]
                      : ct("noWord"),
          },
          {
            type: "primary",
            wide: true,
            onClick: async () => {
              await isConfirming.saveCallback();
              setIsConfirming(
                false,
                "confirm",
                "",
                async () => {},
                async () => {}
              );
            },
            children:
              isConfirming.mode === "delete"
                ? ct("remove")
                : isConfirming.mode === "unsaved"
                  ? ct("unsavedData.saveChanges")
                  : isConfirming.mode === "confirm"
                    ? ct("saveAndContinue")
                    : isConfirming.choices && isConfirming.choices.length > 0
                      ? isConfirming.choices[0]
                      : ct("yes"),
          },
        ]}
      >
        {isConfirming.mode !== "unsaved" &&
          bodyLines.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
      </Modal>
        );
      })()}
      <Modal
        open={isLoading.open}
        header={{
          title: isLoading.message,
          description: isLoading.description,
        }}
        onOpenChange={() => {}}
      >
        <FancyLoader />
      </Modal>
      <Modal
        open={isSaving.open}
        header={{
          title: isSaving.message,
          description: isSaving.description,
        }}
        onOpenChange={() => {}}
      >
        <FancyLoader />
      </Modal>
    </AlertContext.Provider>
  );
};
